// @ts-nocheck
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react-redux/no-unused-prop-types */
import React from 'react';
import cx from 'classnames';
import { isArray } from 'lodash';
import css from './css';
import THEME from './theme';

interface Props {
  children: React.ReactNode;
  href?: string;
  className?: string;
  tabIndex?: number;
  title?: string;
  affirm?: boolean;
  action?: boolean;
  quiet?: boolean;
  destroy?: boolean;
  attention?: boolean;
  info?: boolean;
  controls?: boolean;
  affirmOutlined?: boolean;
  actionOutlined?: boolean;
  quietOutlined?: boolean;
  destroyOutlined?: boolean;
  attentionOutlined?: boolean;
  infoOutlined?: boolean;
  disabled?: boolean;
  text?: boolean;
  textCancel?: boolean;
  tooltip?: string;
  tooltipDelay?: number;
  first?: boolean;
  last?: boolean;
  middle?: boolean;
  small?: boolean;
  icon?: boolean;
  style?: object;
  w50?: boolean;
  w100?: boolean;
  onClick?: (props: any) => void;
}

export default class Button extends React.Component<Props, {}> {
  handleClick = (event) => {
    if (this.props.onClick) {
      event.preventDefault();

      if (!this.props.disabled) {
        this.props.onClick(event);
      }
    }
  };

  buildTestAttribute(): string {
    const { children } = this.props;

    if (!children) {
      return 'undefined';
    }

    // account for some buttons that have text with commas
    const maybeRemoveComma = (index: string | null) => {
      let child = index ? String(children[index]) : String(children);

      if (child.includes(',')) {
        child = child.replace(', ', '-');
      }

      return child.split(' ').join('-');
    };

    // avoids breaking failures from style-wrapped children
    if (typeof children === 'object' && !isArray(children)) {
      return 'undefined';
    }

    // grab icon name passed as children
    if (typeof children[1] === 'object') {
      if (typeof children[1].type === 'function') {
        return children[1].type.name.toLowerCase();
      }

      return children[0].type.name.toLowerCase();
    }

    if (typeof children === 'string') {
      return maybeRemoveComma(null).toLowerCase();
    }

    return maybeRemoveComma('1').toLowerCase();
  }

  propsAndStyle(name) {
    return this.props[name] && styles[name];
  }

  render() {
    const type = cx(
      this.propsAndStyle('affirm'),
      this.propsAndStyle('action'),
      this.propsAndStyle('quiet'),
      this.propsAndStyle('destroy'),
      this.propsAndStyle('attention'),
      this.propsAndStyle('info'),
      this.propsAndStyle('controls'),
      this.propsAndStyle('text'),
      this.propsAndStyle('textCancel'),
      this.propsAndStyle('pressed'),
      this.propsAndStyle('first'),
      this.propsAndStyle('middle'),
      this.propsAndStyle('last'),
    ) || styles.affirm;

    const size = cx(
      this.props.small && styles.small,
      this.props.icon && styles.icon,
      this.props.w50 && styles.w50,
      this.props.w100 && styles.w100,
      this.props.style,
    );

    const disabled = cx(
      this.props.disabled && styles.disabled,
      this.props.style,
    );

    const version = cx(
      this.props.affirmOutlined && styles.affirmOutlined,
      this.props.actionOutlined && styles.actionOutlined,
      this.props.quietOutlined && styles.quietOutlined,
      this.props.destroyOutlined && styles.destroyOutlined,
      this.props.attentionOutlined && styles.attentionOutlined,
      this.props.infoOutlined && styles.infoOutlined,
      this.props.style,
    );

    const allStyles = { ...this.props.style };
    const buttonType = this.props.href ? 'link' : 'button';
    const testAttribute = this.buildTestAttribute(this.props.children);

    return (
      <>
        <a
          role={buttonType}
          style={allStyles}
          className={cx(styles.default, type, size, version, disabled, this.props.className)}
          href={this.props.href || '#'}
          onClick={this.handleClick}
          data-tip={this.props.tooltip}
          data-delay-show={this.props.tooltip && this.props.tooltipDelay}
          data-tip-disable={!this.props.tooltip}
          title={this.props.title}
          tabIndex={this.props.tabIndex}
          data-testid={`${testAttribute}-${buttonType}`}
        >
          {this.props.children}
        </a>
      </>
    );
  }
}

const styles = css({
  default: {
    textAlign: 'center',
    verticalAlign: 'middle',
    display: 'inline-block',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textDecoration: 'none',
    WebkitAppearance: 'none',
    fontSize: '13px',
    fontWeight: 900,
    fontFamily: 'Lato, sans-serif',
    userSelect: 'none',
    padding: '0.6rem 1.2rem',
    border: 'none',
    borderRadius: '5px',
    marginRight: 4,
    lineHeight: 1.5,
    marginTop: '.25rem',
    marginBottom: '.25rem',
    textTransform: 'uppercase',
    color: '#fff',
    fill: '#fff',

    ':hover': {
      color: '#fff',
    },
  },

  text: {
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 0,
    color: '#4baef8',
    fontSize: '13px',
    margin: '0 15px',
    padding: 0,
    transition: 'all 0.15s linear',
    textDecoration: 'none',

    ':hover': {
      color: '#2d95e3',
    },
  },

  textCancel: {
    color: '#7d7d7d',

    ':hover': {
      color: '#41403b',
    },
  },

  textDestroy: {
    color: '#ba2a2a',

    ':hover': {
      color: '#ab2626',
    },
  },

  affirm: {
    backgroundColor: '#4baef8',
    border: '1px solid #4baef8',
    fill: '#fff',

    ':hover': {
      backgroundColor: '#2d95e3',
    },
    ':active': {
      backgroundColor: '#2287d3',
    },
  },

  action: {
    backgroundColor: '#80d657',
    border: '1px solid #80d657',
    fill: '#fff',

    ':hover': {
      backgroundColor: '#6ec048',
    },
    ':active': {
      backgroundColor: '#5ca838',
    },
  },

  quiet: {
    backgroundColor: '#bbbbbb',
    border: '1px solid #bbbbbb',
    fill: '#fff',
    ':hover': {
      backgroundColor: '#acabab',
    },
    ':active': {
      backgroundColor: '#9e9e9e',
    },
  },

  destroy: {
    backgroundColor: '#ba2a2a',
    border: '1px solid #ba2a2a',
    fill: '#fff',

    ':hover': {
      backgroundColor: '#ab2626',
    },
    ':active': {
      backgroundColor: '#941e1e',
    },
  },

  attention: {
    backgroundColor: '#ffc90f',
    border: '1px solid #ffc90f',
    fill: '#fff',
    ':hover': {
      backgroundColor: '#ffbc0f',
    },
    ':active': {
      backgroundColor: '#ff9f0f',
    },
  },

  info: {
    backgroundColor: '#4782a4',
    border: '1px solid #4782a4',
    fill: '#fff',

    ':hover': {
      backgroundColor: '#316c8d',
    },
    ':active': {
      backgroundColor: '#195476',
    },
  },

  controls: {
    backgroundColor: '#fff',
    border: '1px solid #fff',
    color: '#41403b',
    fill: '#41403b',

    ':hover': {
      backgroundColor: '#f7f7f7',
      border: '1px solid #eee',
      color: '#41403b',
    },
    ':active': {
      backgroundColor: '#fff',
      border: '1px solid #eee',
    },
  },

  affirmOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #4baef8',
    color: '#4baef8',
    fill: '#4baef8',

    ':hover': {
      backgroundColor: '#4baef8',
      color: '#fff',
      fill: '#fff',
    },
    ':active': {
      backgroundColor: '#2287d3',
      fill: '#fff',
    },
  },

  actionOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #80d657',
    color: '#80d657',
    fill: '#80d657',

    ':hover': {
      backgroundColor: '#80d657',
    },
    ':active': {
      backgroundColor: '#5ca838',
    },
  },

  quietOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #bbbbbb',
    color: '#41403b',
    fill: '#41403b',

    ':hover': {
      backgroundColor: '#f7f7f7',
      border: '1px solid #bbbbbb',
      color: '#41403b',
      fill: '#41403b',
    },
    ':active': {
      fill: '#41403b',
      backgroundColor: '#f6f6f6',
      border: '1px solid #bbbbbb',
    },
  },

  destroyOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #ba2a2a',
    color: '#ba2a2a',
    fill: '#ba2a2a',

    ':hover': {
      backgroundColor: '#ba2a2a',
      color: '#fff',
      fill: '#fff',
    },
    ':active': {
      backgroundColor: '#941e1e',
      fill: '#fff',
    },
  },

  attentionOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #ffc90f',
    color: '#ff8700',
    fill: '#ff8700',

    ':hover': {
      backgroundColor: '#ffc90f',
      color: '#fff',
      fill: '#fff',
    },
    ':active': {
      backgroundColor: '#ff9f0f',
      fill: '#fff',
    },
  },

  infoOutlined: {
    backgroundColor: '#fff',
    border: '1px solid #4782a4',
    color: '#4782a4',
    fill: '#4782a4',

    ':hover': {
      backgroundColor: '#4782a4',
      color: '#fff',
      fill: '#fff',
    },
    ':active': {
      backgroundColor: '#195476',
      fill: '#fff',
    },
  },

  disabled: {
    cursor: 'default',
    opacity: 0.35,
    pointerEvents: 'none',
  },

  small: {
    padding: '0.35rem 0.9rem',
    fontSize: '11px',
  },

  icon: {
    padding: '0.35rem 0.5rem',
  },

  pressed: {
    backgroundColor: THEME.linkColor,
    color: '#fff',

    ':hover': {
      color: '#fff',
      border: THEME.buttonBorderHighlight,
    },
  },

  first: {
    borderRadius: '0.25rem 0 0 0.25rem',
  },

  last: {
    borderRadius: '0px 0.25rem 0.25rem 0',
    marginLeft: -5,
    borderLeft: '1px solid rgba(0,0,0,.1)',
  },

  middle: {
    borderRadius: 0,
    marginLeft: -5,
  },

  w50: {
    width: '50%',
    minWidth: '-webkit-min-content',
  },

  w100: {
    width: '100%',
    minWidth: '-webkit-min-content',
  },
});
